import { DateTime } from 'luxon';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Notification {
  id: string;
  type: 'booking' | 'cancellation' | 'reschedule';
  appointmentId: string;
  message: string;
  customerName: string;
  appointmentDate: string;
  appointmentTime: string;
  timestamp: string; // ISO string for persistence
  read: boolean;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;

  // Actions
  addNotification: (
    notification: Omit<Notification, 'id' | 'read' | 'timestamp'>,
  ) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    set => ({
      notifications: [],
      unreadCount: 0,

      addNotification: notification => {
        const newNotification: Notification = {
          ...notification,
          id: `${Date.now()}-${Math.random()}`,
          timestamp: DateTime.now().toISO() || '',
          read: false,
        };

        set(state => {
          // Keep only last 50 notifications
          const updatedNotifications = [
            newNotification,
            ...state.notifications,
          ].slice(0, 50);
          const unreadCount = updatedNotifications.filter(n => !n.read).length;

          return {
            notifications: updatedNotifications,
            unreadCount,
          };
        });
      },

      markAsRead: id => {
        set(state => {
          const updatedNotifications = state.notifications.map(n =>
            n.id === id ? { ...n, read: true } : n,
          );
          const unreadCount = updatedNotifications.filter(n => !n.read).length;

          return {
            notifications: updatedNotifications,
            unreadCount,
          };
        });
      },

      markAllAsRead: () => {
        set(state => ({
          notifications: state.notifications.map(n => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },

      removeNotification: id => {
        set(state => {
          const updatedNotifications = state.notifications.filter(
            n => n.id !== id,
          );
          const unreadCount = updatedNotifications.filter(n => !n.read).length;

          return {
            notifications: updatedNotifications,
            unreadCount,
          };
        });
      },

      clearAll: () => {
        set({
          notifications: [],
          unreadCount: 0,
        });
      },
    }),
    {
      name: 'notification-storage', // localStorage key
      partialize: state => ({
        notifications: state.notifications,
        unreadCount: state.unreadCount,
      }),
    },
  ),
);
